/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import { Button, Dropdown, Flex, Modal, notification } from 'antd';

import {
  AntdRouterLink,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteShippingConfig } from 'services';
import type { ShippingConfigSummary } from 'types';
import {
  adjustPageNumber,
  generateTableDataSource,
  localizeShippingAmountType,
  numberFormat,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    SHIPPING_CONFIGS: { UPDATE_URL: SHIPPING_CONFIGS_UPDATE_URL },
  },
} = PATHNAME;

type ShippingConfigsTableProps = {
  loading: boolean;
  total: number;
  shippingConfigs: ShippingConfigSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const ShippingConfigsTable = React.memo<ShippingConfigsTableProps>(
  ({
    loading,
    total,
    shippingConfigs,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteShippingConfig = useLockFn(
      async ({ id }: ShippingConfigSummary) => {
        deleteShippingConfig({ id })
          .then(() => {
            handleRefreshData();
            notification.success({
              message: 'تنظیمات ارسال مورد نظر با موفقیت حذف شد.',
            });
          })
          .catch(message => {
            notification.error({
              message,
            });
          });
      },
    );

    const shippingConfigsDataSource = generateTableDataSource(
      shippingConfigs,
      'id',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 900, y: 700 }}
        columns={[
          {
            title: 'عنوان',
            dataIndex: 'title',
            key: 'title',
            width: '40%',
            ellipsis: true,
            render: (_, { id, title }) => (
              <AntdRouterLink
                ellipsis
                to={`${SHIPPING_CONFIGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {title}
              </AntdRouterLink>
            ),
          },
          {
            title: 'مقدار',
            dataIndex: 'amount',
            key: 'amount',
            width: '40%',
            ellipsis: true,
            render: (_, { amountType, amount }) => {
              const { label } = localizeShippingAmountType(amountType);
              return (
                <span>
                  {numberFormat(amount)} {label}
                </span>
              );
            },
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-shipping-config',
                          label: (
                            <AntdRouterLink
                              to={`${SHIPPING_CONFIGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش تنظیمات ارسال
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-shipping-config',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف تنظیمات ارسال مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteShippingConfig(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>
                                  حذف تنظیمات ارسال
                                </span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={shippingConfigsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
ShippingConfigsTable.displayName = 'ShippingConfigsTable';

export { ShippingConfigsTable };
